import {
  getRevenuePerBedQueryOptions,
  getRevenuePerResidentScopedQueryOptions,
} from '@/entities/facility/api/get-revenue-per-unit.query';
import { FocusSummaryCard } from '@/shared/components';
import { formatCurrency } from '@/shared/lib/currency';
import { useFacilityScope } from '@/shared/lib/hooks/use-facility-scope';
import { useQuery } from '@tanstack/react-query';
import type { Dayjs } from 'dayjs';
import type { FC } from 'react';

type RevenuePerUnitCardProps = {
  dateRange: {
    from: Dayjs;
    to: Dayjs;
  };
};

const useRevenuePerUnit = (
  dateRange: RevenuePerUnitCardProps['dateRange'],
  unit: 'resident' | 'bed'
) => {
  const { scopedFacilityId } = useFacilityScope();

  const params = {
    from: dateRange.from.format('YYYY-MM-DD'),
    to: dateRange.to.format('YYYY-MM-DD'),
    facilitiesIds: scopedFacilityId ? [scopedFacilityId] : undefined,
  };

  return useQuery(
    unit === 'bed'
      ? getRevenuePerBedQueryOptions(params)
      : getRevenuePerResidentScopedQueryOptions(params)
  );
};

/**
 * Revenue per Resident / per Bed on the Facilities Financial tab (ST #137).
 *
 * Figma shows a bare value with a change indicator and no sparkline, so these
 * skip `showChart` even though their siblings in Row 1 carry one.
 */
const RevenuePerUnitCard: FC<
  RevenuePerUnitCardProps & {
    unit: 'resident' | 'bed';
    title: string;
    tooltipMessage: string;
  }
> = ({ dateRange, unit, title, tooltipMessage }) => {
  const { data, isLoading } = useRevenuePerUnit(dateRange, unit);

  const indicator =
    data?.changePct != null
      ? { value: data.changePct, negativeValue: data.changePct < 0 }
      : undefined;

  return (
    <FocusSummaryCard
      icon="team"
      title={title}
      indicator={indicator}
      tooltipMessage={tooltipMessage}
      value={data?.amountUsd != null ? formatCurrency(data.amountUsd) : 'N/A'}
      loading={isLoading}
    />
  );
};

export const RevenuePerResidentCard: FC<RevenuePerUnitCardProps> = (props) => (
  <RevenuePerUnitCard
    {...props}
    unit="resident"
    title="Revenue per Resident"
    tooltipMessage="Average revenue generated per resident during the selected period. Calculated as: Total Revenue ÷ Average Number of Residents"
  />
);

export const RevenuePerBedCard: FC<RevenuePerUnitCardProps> = (props) => (
  <RevenuePerUnitCard
    {...props}
    unit="bed"
    title="Revenue per Bed"
    tooltipMessage="Average revenue generated per bed during the selected period. Calculated as: Total Revenue ÷ Average Number of Beds"
  />
);
